import { Component } from '@opten/gin-tonic/helpers/compiler';

@Component({ className: 'js-accordion' })
export class AccordionComponent {
	private items: HTMLElement[] = [];

	constructor(private element: HTMLElement) {
		this.items = Array.from(
			this.element.querySelectorAll('[data-accordion-item]')
		) as HTMLElement[];
		this.bind();
	}

	private bind() {
		this.items.forEach((item) => {
			const trigger = item.querySelector(
				'[data-accordion-trigger]'
			) as HTMLButtonElement | null;
			const panel = item.querySelector(
				'.c-accordion-item__panel'
			) as HTMLElement | null;
			if (!trigger || !panel) return;

			trigger.addEventListener('click', () =>
				this.toggle(item, trigger, panel)
			);
			// Optional: allow keyboard left/up to move to prev, right/down to next
			trigger.addEventListener('keydown', (e) => this.onKeyNav(e, trigger));
		});
	}

	private toggle(
		item: HTMLElement,
		trigger: HTMLButtonElement,
		panel: HTMLElement
	) {
		const expanded = trigger.getAttribute('aria-expanded') === 'true';
		// Smooth-close all other open panels
		this.items.forEach((it) => {
			if (it === item) return;
			const otherTrigger = it.querySelector(
				'[data-accordion-trigger]'
			) as HTMLButtonElement | null;
			const otherPanel = it.querySelector(
				'.c-accordion-item__panel'
			) as HTMLElement | null;
			if (!otherTrigger || !otherPanel) return;
			if (otherTrigger.getAttribute('aria-expanded') === 'true') {
				otherTrigger.setAttribute('aria-expanded', 'false');
				this.animateClose(otherPanel);
			}
		});

		if (!expanded) {
			trigger.setAttribute('aria-expanded', 'true');
			panel.hidden = false;
			this.animateOpen(panel);
		} else {
			trigger.setAttribute('aria-expanded', 'false');
			this.animateClose(panel);
		}
	}

	private animateOpen(panel: HTMLElement) {
		const inner = panel.querySelector(
			'.c-accordion-item__panel-inner'
		) as HTMLElement | null;
		if (!inner) {
			panel.hidden = false;
			return;
		}
		panel.hidden = false;
		panel.style.height = '0px';
		panel.style.opacity = '0';
		const target =
			inner.offsetHeight + parseFloat(getComputedStyle(inner).marginTop || '0');
		requestAnimationFrame(() => {
			panel.style.height = target + 'px';
			panel.style.opacity = '1';
		});
		panel.addEventListener('transitionend', function handler(ev) {
			if (ev.propertyName === 'height') {
				panel.style.height = 'auto';
				panel.removeEventListener('transitionend', handler);
			}
		});
	}

	private animateClose(panel: HTMLElement) {
		if (panel.hidden) return; // already closed
		// If previously set to auto (after open), lock current pixel height to enable transition
		const currentHeight = panel.offsetHeight;
		panel.style.height = currentHeight + 'px';
		panel.style.opacity = '1';
		requestAnimationFrame(() => {
			panel.style.height = '0px';
			panel.style.opacity = '0';
		});
		panel.addEventListener('transitionend', function handler(ev) {
			if (ev.propertyName === 'height') {
				panel.hidden = true; // hide after animation for accessibility
				panel.removeEventListener('transitionend', handler);
			}
		});
	}

	private onKeyNav(e: KeyboardEvent, trigger: HTMLButtonElement) {
		const code = e.key;
		const triggers = this.items
			.map(
				(it) =>
					it.querySelector(
						'[data-accordion-trigger]'
					) as HTMLButtonElement | null
			)
			.filter(Boolean) as HTMLButtonElement[];
		const idx = triggers.indexOf(trigger);
		if (code === 'ArrowDown' || code === 'ArrowRight') {
			e.preventDefault();
			const next = triggers[(idx + 1) % triggers.length];
			next.focus();
		} else if (code === 'ArrowUp' || code === 'ArrowLeft') {
			e.preventDefault();
			const prev = triggers[(idx - 1 + triggers.length) % triggers.length];
			prev.focus();
		} else if (code === 'Home') {
			e.preventDefault();
			triggers[0].focus();
		} else if (code === 'End') {
			e.preventDefault();
			triggers[triggers.length - 1].focus();
		} else if (code === 'Enter' || code === ' ') {
			e.preventDefault();
			trigger.click();
		}
	}
}

// Auto-init fallback if not using component loader
(function init() {
	const nodes = document.querySelectorAll<HTMLElement>(
		'[data-component="accordion"]'
	);
	nodes.forEach((el) => new AccordionComponent(el));
})();
